import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Divider } from "@mui/material";

import { IconNameEnum } from "../../../components/Icon";
import { SliderVariant } from "../../../components/Slider";
import { TextInput } from "../../../components/TextInput";
import {
  BILLING_WEIGHTED_FLAT_FEE_MAX,
  BILLING_WEIGHTED_FLAT_FEE_MAX_DECIMAL_PLACES,
  BILLING_WEIGHTED_FLAT_FEE_MIN,
  BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MAX_DECIMAL_PLACES,
  BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MIN,
  BILLING_WEIGHTED_RATE_MAX,
  BILLING_WEIGHTED_RATE_MAX_DECIMAL_PLACES,
  BILLING_WEIGHTED_RATE_MIN,
} from "../../../consts/billing";
import { updateBillingWeightedField } from "../../../reducers/billingReducer";
import {
  getBillingHouseholdWithFixedOnlyCount,
  getBillingIsUpdating,
  getBillingShouldDisplayMinWastePerHouseholdField,
  getBillingWeighted,
  getBillingWeightedMinWastePerHouseholdMax,
  getBillingWeightedMinWastePerHouseholdSuggested,
  getIsBillingInNewStatus,
} from "../../../selectors/billing";
import { useAppDispatch } from "../../../store";
import { Weight } from "../../../types/weights";
import { formatNumber } from "../../../utils/number";
import { BillingFeeInput } from "../BillingFeeInput";

import styles from "./BillingWeightedFeeCalculation.module.scss";

export const BillingWeightedFeeCalculation: React.FC = () => {
  const { t } = useTranslation();

  const isBillingInNewStatus = useSelector(getIsBillingInNewStatus);
  const isUpdating = useSelector(getBillingIsUpdating);
  const dispatch = useAppDispatch();
  const {
    rate,
    suggestedRate,
    flatFee,
    suggestedFlatFee,
    avgPublicWastePerPerson,
    lastYearsWasteSum,
    minWastePerHousehold,
    medianWastePerHousehold,
  } = useSelector(getBillingWeighted);
  const shouldDisplayMinWastePerHouseholdField = useSelector(getBillingShouldDisplayMinWastePerHouseholdField);
  const weightedMinWastePerHouseholdMax = useSelector(getBillingWeightedMinWastePerHouseholdMax);
  const weightedMinWastePerHouseholdSuggested = useSelector(getBillingWeightedMinWastePerHouseholdSuggested);
  const householdWithFixedOnlyCount = useSelector(getBillingHouseholdWithFixedOnlyCount);

  const isDisabled = isUpdating || !isBillingInNewStatus;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h5 className={styles.title}>{t("billingWeightedFeeCalculation.flatFeeTitle")}</h5>
          <p className={styles.subtitle}>
            {t("billingWeightedFeeCalculation.householdsInFlatFee")}{" "}
            <span className={styles.highlightedValue}>{householdWithFixedOnlyCount}</span>
          </p>
        </div>
        <div className={styles.inputsRow}>
          <BillingFeeInput
            value={flatFee}
            defaultValue={suggestedFlatFee}
            onChange={(value) => dispatch(updateBillingWeightedField({ field: "flatFee", value }))}
            iconName={IconNameEnum.person}
            sliderVariant={SliderVariant.Orange}
            label={t("billingWeightedFeeCalculation.flatFeeLabel")}
            description={t("billingWeightedFeeCalculation.flatFeeDescription")}
            minValue={BILLING_WEIGHTED_FLAT_FEE_MIN}
            maxValue={BILLING_WEIGHTED_FLAT_FEE_MAX}
            maxDecimalPlaces={BILLING_WEIGHTED_FLAT_FEE_MAX_DECIMAL_PLACES}
            isDisabled={isDisabled}
          ></BillingFeeInput>
          <Divider orientation="vertical" flexItem />
          <div>
            <p className={styles.inputLabel}>{t("billingWeightedFeeCalculation.averageUnweighedWaste")}</p>
            <TextInput
              value={formatNumber(avgPublicWastePerPerson)}
              isDisabled
              endItem={<div className={styles.unit}>{Weight.Kg}</div>}
            />
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <h5 className={styles.title}>{t("billingWeightedFeeCalculation.rateTitle")}</h5>
        {shouldDisplayMinWastePerHouseholdField && (
          <div className={styles.inputsRow}>
            <BillingFeeInput
              value={minWastePerHousehold}
              defaultValue={weightedMinWastePerHouseholdSuggested}
              onChange={(value) => dispatch(updateBillingWeightedField({ field: "minWastePerHousehold", value }))}
              label={t("billingWeightedFeeCalculation.minWastePerHouseholdLabel")}
              description={t("billingWeightedFeeCalculation.minWastePerHouseholdDescription")}
              minValue={BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MIN}
              maxValue={weightedMinWastePerHouseholdMax}
              maxDecimalPlaces={BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MAX_DECIMAL_PLACES}
              isDisabled={isDisabled}
              unit={Weight.Kg}
            />
            <Divider orientation="vertical" flexItem />
            <div>
              <p className={styles.inputLabel}>{t("billingWeightedFeeCalculation.medianWastePerHousehold")}</p>
              <TextInput
                value={formatNumber(medianWastePerHousehold)}
                isDisabled
                endItem={<div className={styles.unit}>{Weight.Kg}</div>}
              />
            </div>
          </div>
        )}
        <div className={styles.inputsRow}>
          <BillingFeeInput
            value={rate}
            defaultValue={suggestedRate}
            onChange={(value) => dispatch(updateBillingWeightedField({ field: "rate", value }))}
            label={t("billingWeightedFeeCalculation.rateLabel")}
            description={t("billingWeightedFeeCalculation.rateDescription")}
            minValue={BILLING_WEIGHTED_RATE_MIN}
            maxValue={BILLING_WEIGHTED_RATE_MAX}
            maxDecimalPlaces={BILLING_WEIGHTED_RATE_MAX_DECIMAL_PLACES}
            isDisabled={isDisabled}
          />
          <Divider orientation="vertical" flexItem />
          <div>
            <p className={styles.inputLabel}>{t("billingWeightedFeeCalculation.minWasteAmount")}</p>
            <TextInput
              value={formatNumber(lastYearsWasteSum)}
              isDisabled
              endItem={<div className={styles.unit}>{Weight.Kg}</div>}
            />
          </div>
        </div>
      </div>
    </>
  );
};
